import { Button, Checkbox, Group, Menu, Modal, SegmentedControl, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconFileTypeCsv, IconFileTypePdf, IconFileSpreadsheet, IconPrinter } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { downloadFile, http } from "../api/http";
import { useCan } from "../auth/user";
import { useT } from "../i18n/script";

export interface IssueOptions { script?: "lat" | "cyr"; number?: string | null; purpose?: string | null }

export async function runReport(code: string, params: Record<string, unknown>, format: string, archive = false, opts: IssueOptions = {}) {
  let script = opts.script;
  if (!script) {
    try { script = (localStorage.getItem("agrisense.script") as "lat" | "cyr") || "lat"; } catch { script = "lat"; }
  }
  return downloadFile(`/api/v1/reports/templates/${code}/run`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ params, format, archive, script, number: opts.number || null, purpose: opts.purpose ?? null }),
  }, format === "pdf" || format === "html");
}

/** Podaci o izdavanju: broj, svrha (za šta se izdaje), pismo i arhiviranje u DMS. */
export function IssueFields({ value, onChange, archive, setArchive }: {
  value: IssueOptions; onChange: (v: IssueOptions) => void;
  archive: boolean; setArchive?: (v: boolean) => void;
}) {
  const { t } = useT();
  const can = useCan();
  return (
    <Stack gap="sm">
      <Group grow align="flex-start">
        <TextInput label={t("Broj dokumenta")} disabled={archive} value={archive ? "" : value.number ?? ""}
          placeholder={archive ? t("dodjeljuje djelovodnik pri arhiviranju") : t("npr. 12/2.1-000-87/26")}
          description={t("Broj iz djelovodnika ili drugi; prazno = mjesto za upis rukom")}
          onChange={(e) => onChange({ ...value, number: e.currentTarget.value })} />
        <div>
          <Text size="sm" fw={500}>{t("Pismo dokumenta")}</Text>
          <Text size="xs" c="dimmed" mb={6}>{t("Zaglavlje, tekst i tabele")}</Text>
          <SegmentedControl value={value.script ?? "lat"} onChange={(v) => onChange({ ...value, script: v as "lat" | "cyr" })}
            data={[{ value: "lat", label: "Latinica" }, { value: "cyr", label: "Ћирилица" }]} />
        </div>
      </Group>
      <Textarea label={t("Svrha — za šta se dokument izdaje")} autosize minRows={2} maxRows={6} value={value.purpose ?? ""}
        onChange={(e) => onChange({ ...value, purpose: e.currentTarget.value })}
        description={t("Ispisuje se ispod naslova. Podaci (proizvođač, voćnjak, sezona…) dodaju se automatski.")} />
      {setArchive && can("dms:write") && (
        <Checkbox label={t("Zavedi i arhiviraj u DMS (broj iz djelovodnika, rok čuvanja)")} checked={archive}
          onChange={(e) => setArchive(e.currentTarget.checked)} />
      )}
      <Text size="xs" c="dimmed">{t("Zaglavlje sa logom i nazivom Agencije, datum izdavanja i izdavalac (vaše ime) dodaju se automatski; podešavaju se u Administracija → Podešavanja → Izlazni dokumenti.")}</Text>
    </Stack>
  );
}

/** Podrazumijevana svrha dokumenta za izvještaj (sa ubačenom sezonom i sl.). */
export function usePurpose(code: string, params: Record<string, unknown>, enabled = true) {
  const q = useQuery({ queryKey: ["report-tpl", code], enabled, staleTime: 600_000,
    queryFn: () => http.get<{ purpose_default?: string }>(`/api/v1/reports/templates/${code}`) });
  let txt = q.data?.purpose_default ?? "";
  for (const [k, v] of Object.entries(params)) txt = txt.replaceAll(`{${k}}`, v == null ? "" : String(v));
  return txt.replace(/\{[a-z_]+\}/g, "").replace(/\s{2,}/g, " ");
}

/** Dugme za izvršavanje izvještaja (PDF uz podatke o izdavanju / Excel / CSV, opciono arhiviranje u DMS). */
export function ReportButton({ code, params, label = "Izvještaj", variant = "light", size = "sm" }: {
  code: string; params: Record<string, unknown>; label?: string; variant?: string; size?: string;
}) {
  const { t, script } = useT();
  const [busy, setBusy] = useState(false);
  const [issue, setIssue] = useState(false);
  const [opts, setOpts] = useState<IssueOptions>({ script });
  const [archive, setArchive] = useState(false);
  const purpose = usePurpose(code, params, issue);
  const go = async (fmt: string, arch = false, o: IssueOptions = { script }) => {
    setBusy(true);
    try {
      const r = await runReport(code, params, fmt, arch, o);
      if (arch) notifications.show({ color: "green", message: t(`Dokument zaveden i arhiviran u DMS (dokument ${r.headers.get("X-Document-Id")})`) });
      setIssue(false);
    } catch (e) {
      notifications.show({ color: "red", title: t("Izvještaj"), message: (e as Error).message });
    } finally { setBusy(false); }
  };
  const openIssue = () => { setOpts({ script, number: "", purpose: null }); setArchive(false); setIssue(true); };
  return (
    <>
      <Menu position="bottom-end">
        <Menu.Target><Button variant={variant} size={size} loading={busy && !issue} leftSection={<IconPrinter size={16} />}>{t(label)}</Button></Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconFileTypePdf size={16} />} onClick={openIssue}>{t("PDF dokument…")}</Menu.Item>
          <Menu.Item leftSection={<IconFileSpreadsheet size={16} />} onClick={() => go("xlsx")}>Excel (XLSX)</Menu.Item>
          <Menu.Item leftSection={<IconFileTypeCsv size={16} />} onClick={() => go("csv")}>CSV</Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <Modal opened={issue} onClose={() => setIssue(false)} title={t("Izdavanje dokumenta")} size="lg">
        <Stack>
          <IssueFields archive={archive} setArchive={setArchive}
            value={{ ...opts, purpose: opts.purpose ?? purpose }} onChange={setOpts} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setIssue(false)}>{t("Odustani")}</Button>
            <Button leftSection={<IconFileTypePdf size={16} />} loading={busy}
              onClick={() => go("pdf", archive, { ...opts, purpose: opts.purpose ?? purpose })}>{t("Izdaj PDF")}</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
